const ISTANBUL_TIME_ZONE = "Europe/Istanbul"
const CUTOFF_HOUR = 14
const CUTOFF_UTC_HOUR = 11

export type ShippingSchedule = {
  order: string
  shipping: string
  delivery: string
  cutoffAt: Date
  cutoffShipLabel: "bugün" | "yarın"
}

type CalendarDate = {
  year: number
  month: number
  day: number
}

type LocalDateTime = CalendarDate & {
  hour: number
  minute: number
  second: number
}

const dateTimeFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: ISTANBUL_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
})

const displayDateFormatter = new Intl.DateTimeFormat("tr-TR", {
  timeZone: ISTANBUL_TIME_ZONE,
  day: "numeric",
  month: "short",
})

const normalizeOffset = (value: number) =>
  Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0

const getLocalDateTime = (date: Date): LocalDateTime => {
  const parts = Object.fromEntries(
    dateTimeFormatter
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)])
  )

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
  }
}

const addCalendarDays = (date: CalendarDate, days: number): CalendarDate => {
  const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + days, 12))

  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  }
}

const sameCalendarDate = (left: CalendarDate, right: CalendarDate) =>
  left.year === right.year && left.month === right.month && left.day === right.day

const dayOfWeek = (date: CalendarDate) =>
  new Date(Date.UTC(date.year, date.month - 1, date.day, 12)).getUTCDay()

const toIstanbulCutoff = (date: CalendarDate) =>
  new Date(Date.UTC(date.year, date.month - 1, date.day, CUTOFF_UTC_HOUR, 0, 0, 0))

const formatCalendarDate = (date: CalendarDate) =>
  displayDateFormatter.format(
    new Date(Date.UTC(date.year, date.month - 1, date.day, 12, 0, 0, 0))
  )

export function resolveShippingSchedule(
  now: Date,
  toShippingDays: number,
  toDeliveryDays: number
): ShippingSchedule {
  const local = getLocalDateTime(now)
  const today = { year: local.year, month: local.month, day: local.day }
  const isSunday = dayOfWeek(today) === 0
  const isBeforeCutoff = local.hour < CUTOFF_HOUR

  const shippingOffset =
    !isSunday && isBeforeCutoff ? 0 : Math.max(1, normalizeOffset(toShippingDays))
  const deliveryOffset = Math.max(shippingOffset, normalizeOffset(toDeliveryDays))

  const shippingDate = addCalendarDays(today, shippingOffset)
  const deliveryStartDate = addCalendarDays(today, deliveryOffset)
  const deliveryEndDate = addCalendarDays(deliveryStartDate, 2)
  const tomorrow = addCalendarDays(today, 1)
  const formatRelativeDate = (date: CalendarDate) =>
    sameCalendarDate(date, today)
      ? "Bugün"
      : sameCalendarDate(date, tomorrow)
        ? "Yarın"
        : formatCalendarDate(date)
  const delivery = `${formatCalendarDate(deliveryStartDate)} - ${formatCalendarDate(deliveryEndDate)}`

  const cutoffDayOffset = isSunday ? 1 : isBeforeCutoff ? 0 : 1
  const cutoffDate = addCalendarDays(today, cutoffDayOffset)

  return {
    order: "Bugün",
    shipping: formatRelativeDate(shippingDate),
    delivery,
    cutoffAt: toIstanbulCutoff(cutoffDate),
    cutoffShipLabel: cutoffDayOffset === 0 ? "bugün" : "yarın",
  }
}
